import type { ScanMode } from "@/hooks/usePackageScan";

/**
 * The durable "a scan is (or was just) in flight" marker + the pure restore
 * decision, backing scan resume across SPA navigation AND hard reloads.
 *
 * The scan itself always survives leaving the page — the edge function runs to
 * completion server-side and persists the package_scans row — but the page's
 * state dies with the component. This marker is written at the Scan click
 * (progressId + storage_path exist BEFORE the invoke), enriched by unmount-safe
 * callbacks as the scan progresses (scanId at success, error on failure), and
 * read by the scanner page on every mount to decide what to restore.
 *
 * v1 markers ({ scanId, fileName, ts }, written by the old reload-only restore)
 * still parse, so an in-place deploy doesn't strand a fresh one.
 */

export const INFLIGHT_KEY = "cr-scan-inflight";
export const INFLIGHT_TTL_MS = 30 * 60 * 1000; // don't auto-restore a scan older than 30 min

export interface InflightScanMarker {
  v: 2;
  /** Client-generated scan_progress row id; "" on a parsed legacy v1 marker. */
  progressId: string;
  /** Storage path of the upload — the backend keys the scan row on it, so it is
   *  the resume lookup when the row id was never learned. */
  storagePath?: string;
  /** package_scans row id; learned at buffered-response success (hook-level). */
  scanId?: string;
  fileName: string | null;
  pageCount?: number | null;
  mode?: ScanMode;
  entityType?: "carrier" | "broker";
  showPins?: boolean;
  /** Failure message stamped by the hook-level onError (post-unmount safe). */
  error?: string;
  /** Scan start (Date.now()). */
  ts: number;
}

export function saveInflightStart(marker: InflightScanMarker): void {
  try {
    localStorage.setItem(INFLIGHT_KEY, JSON.stringify(marker));
  } catch {
    /* storage unavailable */
  }
}

/** Patch the stored marker — but only while it still belongs to the same scan.
 *  Late callbacks from a superseded scan (the user already started another) must
 *  never overwrite the new scan's marker. */
export function updateInflightScan(
  patch: Partial<InflightScanMarker>,
  expectedProgressId: string,
): void {
  try {
    const cur = readInflightScan();
    if (!cur || cur.progressId !== expectedProgressId) return;
    localStorage.setItem(INFLIGHT_KEY, JSON.stringify({ ...cur, ...patch }));
  } catch {
    /* storage unavailable */
  }
}

export function clearInflightScan(): void {
  try {
    localStorage.removeItem(INFLIGHT_KEY);
  } catch {
    /* storage unavailable */
  }
}

export function readInflightScan(): InflightScanMarker | null {
  try {
    const v = JSON.parse(localStorage.getItem(INFLIGHT_KEY) || "null");
    if (!v || typeof v !== "object") return null;
    const o = v as Record<string, unknown>;
    if (typeof o.ts !== "number") return null;
    if (o.v === 2) {
      if (typeof o.progressId !== "string") return null;
      return o as unknown as InflightScanMarker;
    }
    // Legacy v1 { scanId, fileName, ts } from the reload-only restore.
    if (typeof o.scanId === "string") {
      return {
        v: 2,
        progressId: "",
        scanId: o.scanId,
        fileName: typeof o.fileName === "string" ? o.fileName : null,
        ts: o.ts,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export type RestoreDecision =
  | { kind: "none" }
  | { kind: "clear" }
  /** Terminal non-error row with its document still in Storage → restored result view. */
  | { kind: "result" }
  /** The scan failed while the user was away → surface it, then clear. */
  | { kind: "error"; message: string }
  /** The scan is (as far as we can tell) still running → live re-attach. */
  | { kind: "attach" };

const TERMINAL_OK = new Set(["extracted", "needs_review"]);

/**
 * Pure mount-time decision: given the stored marker and the freshest row fetch,
 * what should the scanner page do? Server truth outranks the marker's error
 * stamp — a client-side transport failure can stamp `error` while the scan keeps
 * running (and finishing) server-side.
 */
export function decideScanRestore(
  marker: InflightScanMarker | null,
  row: { status?: string | null; storage_path?: string | null; error?: string | null } | null,
  now: number,
): RestoreDecision {
  if (!marker) return { kind: "none" };
  if (now - marker.ts > INFLIGHT_TTL_MS) return { kind: "clear" };
  if (row) {
    if (TERMINAL_OK.has(String(row.status))) {
      return row.storage_path ? { kind: "result" } : { kind: "clear" };
    }
    if (row.status === "error") {
      return { kind: "error", message: row.error || "The scan could not be completed." };
    }
    // pending / running (or an unknown forward-compat status) → watch it live.
    return { kind: "attach" };
  }
  // No row found. A stamped failure explains it (quota block, upload error, …).
  if (marker.error) return { kind: "error", message: marker.error };
  // No row YET (row claim happens after upload) — attach if we hold any handle
  // to watch it by; a bare legacy marker with nothing to watch just clears.
  if (marker.progressId || marker.storagePath || marker.scanId) return { kind: "attach" };
  return { kind: "clear" };
}
